'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  label: string;
  value: string;
}

interface FilterCategory {
  name: string;
  label: string;
  options: FilterOption[];
  type: 'select' | 'checkbox' | 'range';
}

interface PuppyFilters {
  variety?: string;
  gender?: string;
  ageRange?: string;
  priceRange?: [number, number];
}

interface PuppyFilterInterfaceProps {
  onFilterChange: (filters: PuppyFilters) => void;
  initialFilters?: PuppyFilters;
}

const PuppyFilterInterface = ({
  onFilterChange,
  initialFilters = {},
}: PuppyFilterInterfaceProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [filters, setFilters] = useState<PuppyFilters>(initialFilters);

  const filterCategories: FilterCategory[] = [
    {
      name: 'variety',
      label: 'Variety',
      type: 'select',
      options: [
        { label: 'All Varieties', value: '' },
        { label: 'Yellow Labrador', value: 'yellow' },
        { label: 'Black Labrador', value: 'black' },
        { label: 'Chocolate Labrador', value: 'chocolate' },
      ],
    },
    {
      name: 'gender',
      label: 'Gender',
      type: 'select',
      options: [
        { label: 'All Genders', value: '' },
        { label: 'Male', value: 'male' },
        { label: 'Female', value: 'female' },
      ],
    },
    {
      name: 'ageRange',
      label: 'Age Range',
      type: 'select',
      options: [
        { label: 'All Ages', value: '' },
        { label: '8-12 weeks', value: '8-12' },
        { label: '12-16 weeks', value: '12-16' },
        { label: '16+ weeks', value: '16+' },
      ],
    },
  ];

  const handleFilterChange = (name: string, value: string) => {
    const newFilters = { ...filters, [name]: value || undefined };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: PuppyFilters = {};
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters).some(
    (value) => value !== undefined && value !== ''
  );

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="rounded-xl bg-card shadow-warm">
      {/* Filter Header */}
      <div className="flex items-center justify-between border-b border-border p-5">
        <div className="flex items-center gap-3">
          <Icon name="FunnelIcon" size={24} variant="outline" className="text-primary" />
          <h3 className="font-heading text-lg font-semibold text-foreground">
            Filter Puppies
          </h3>
          {hasActiveFilters && (
            <span className="rounded-full bg-accent px-2.5 py-0.5 text-xs font-medium text-accent-foreground">
              Active
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-smooth hover:bg-muted hover:text-foreground"
            >
              <Icon name="XMarkIcon" size={16} />
              Clear
            </button>
          )}

          <button
            onClick={toggleExpanded}
            className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-smooth hover:bg-muted hover:text-foreground lg:hidden"
            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
          >
            <Icon
              name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
              size={20}
            />
          </button>
        </div>
      </div>

      {/* Filter Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isExpanded ? 'max-h-[500px]' : 'max-h-0 lg:max-h-[500px]'
        }`}
      >
        <div className="grid gap-5 p-5 md:grid-cols-3">
          {filterCategories.map((category) => (
            <div key={category.name}>
              <label
                htmlFor={category.name}
                className="mb-2 block text-sm font-medium text-foreground"
              >
                {category.label}
              </label>

              {category.type === 'select' && (
                <select
                  id={category.name}
                  name={category.name}
                  value={filters[category.name as keyof PuppyFilters] as string || ''}
                  onChange={(e) => handleFilterChange(category.name, e.target.value)}
                  className="w-full rounded-md border border-border bg-input px-4 py-2.5 text-foreground transition-smooth hover:border-primary/30 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
                >
                  {category.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ))}
        </div>

        {/* Price Range Slider */}
        <div className="border-t border-border p-5">
          <label className="mb-3 block text-sm font-medium text-foreground">
            Price Range
          </label>
          <div className="flex items-center gap-4">
            <input
              type="number"
              placeholder="Min"
              className="w-full rounded-md border border-border bg-input px-4 py-2.5 text-foreground transition-smooth hover:border-primary/30 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
              min="0"
              step="100"
            />
            <span className="text-muted-foreground">to</span>
            <input
              type="number"
              placeholder="Max"
              className="w-full rounded-md border border-border bg-input px-4 py-2.5 text-foreground transition-smooth hover:border-primary/30 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
              min="0"
              step="100"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PuppyFilterInterface;